<template>
  <div class="completar-wrapper">
    <div class="bg-orb bg-orb-1"></div>
    <div class="bg-orb bg-orb-2"></div>

    <!-- Loading -->
    <div v-if="estado === 'loading'" class="completar-center">
      <div class="state-card glass-strong">
        <v-icon dark size="40" color="rgba(255,255,255,0.4)" class="spin"
          >mdi-loading</v-icon
        >
        <p>Carregando...</p>
      </div>
    </div>

    <!-- Link inválido / já completado -->
    <div v-else-if="estado === 'invalido'" class="completar-center">
      <div class="state-card glass-strong">
        <v-icon dark size="52" color="rgba(231,76,60,0.7)">mdi-link-off</v-icon>
        <h3>Link inválido</h3>
        <p>
          Este link já foi utilizado ou não é válido. Se precisar de ajuda,
          entre em contato com a clínica.
        </p>
        <router-link to="/" class="btn-primary state-btn">
          <v-icon dark size="15">mdi-home-outline</v-icon>
          <span style="color: white">Ir para o início</span>
        </router-link>
      </div>
    </div>

    <!-- Sucesso -->
    <div v-else-if="estado === 'sucesso'" class="completar-center">
      <div class="state-card glass-strong success-card">
        <div class="success-check">
          <v-icon dark size="52" color="#2cb7b1">mdi-check-decagram</v-icon>
        </div>
        <h3>Cadastro concluído!</h3>
        <p>
          As informações de <strong>{{ form.Nome }}</strong> foram salvas com
          sucesso. O sistema Fino Trato agora protege seu pet!
        </p>
        <router-link
          :to="`/consultar-pet/${microchipId}`"
          class="btn-primary state-btn"
          v-if="microchipId"
        >
          <v-icon dark size="15">mdi-paw</v-icon>
          Ver página do pet
        </router-link>
        <router-link to="/" class="home-link-sm">Ir para o início</router-link>
      </div>
    </div>

    <!-- Formulário -->
    <div v-else-if="estado === 'form'" class="completar-center completar-wide">
      <div class="completar-card glass-strong">
        <!-- Topo -->
        <div class="completar-header">
          <img src="@/assets/logo-white.png" class="completar-logo" />
          <div>
            <h2 class="completar-title">Complete o cadastro do seu pet</h2>
            <p class="completar-sub">
              Microchip: <strong class="chip-code">{{ microchipId }}</strong>
            </p>
          </div>
        </div>

        <div class="completar-divider"></div>

        <!-- Upload de foto -->
        <div class="foto-section" @click="$refs.fotoInput.click()">
          <div class="foto-preview-wrap" :class="{ 'has-foto': !!fotoPreview }">
            <img v-if="fotoPreview" :src="fotoPreview" class="foto-preview" />
            <div class="foto-placeholder" v-else>
              <v-icon dark size="32" color="rgba(255,255,255,0.4)"
                >mdi-camera-plus-outline</v-icon
              >
              <span>Adicionar foto do pet</span>
            </div>
            <div class="foto-overlay">
              <v-icon dark size="22" color="white">mdi-camera</v-icon>
              <span>{{ fotoPreview ? "Trocar foto" : "Adicionar foto" }}</span>
            </div>
          </div>
          <input
            ref="fotoInput"
            type="file"
            accept="image/*"
            style="display: none"
            @change="onFotoChange"
          />
        </div>

        <!-- Formulário -->
        <form @submit.prevent="salvar" class="completar-form">
          <div class="form-grid">
            <div class="form-group full">
              <label>Nome do Pet *</label>
              <input
                v-model="form.Nome"
                required
                class="glass-input"
                placeholder="Ex: Thor"
              />
            </div>
            <div class="form-group">
              <label>Tipo de Pet</label>
              <select v-model="form.TipoPet" class="glass-input">
                <option value="">Selecionar...</option>
                <option>Cachorro</option>
                <option>Gato</option>
                <option>Pássaro</option>
                <option>Outro</option>
              </select>
            </div>
            <div class="form-group">
              <label>Raça</label>
              <input
                v-model="form.Raca"
                class="glass-input"
                placeholder="Ex: Golden Retriever"
              />
            </div>
            <div class="form-group">
              <label>Sexo</label>
              <select v-model="form.Sexo" class="glass-input">
                <option value="">Selecionar...</option>
                <option>Macho</option>
                <option>Fêmea</option>
              </select>
            </div>
            <div class="form-group">
              <label>Idade (anos)</label>
              <input
                v-model.number="form.Idade"
                type="number"
                min="0"
                max="30"
                class="glass-input"
                placeholder="0"
              />
            </div>
            <div class="form-group">
              <label>Data de Nascimento</label>
              <input
                v-model="form.DataNascimento"
                type="date"
                class="glass-input"
              />
            </div>
            <div class="form-group">
              <label>Coloração</label>
              <input
                v-model="form.Coloracao"
                class="glass-input"
                placeholder="Ex: Dourado"
              />
            </div>
            <div class="form-group">
              <label>Vacinas</label>
              <input
                v-model="form.Vacinas"
                class="glass-input"
                placeholder="Antirrábica, V10..."
              />
            </div>
            <div class="form-group full">
              <label>Nome do Tutor / Responsável *</label>
              <input
                v-model="form.Portador"
                required
                class="glass-input"
                placeholder="Seu nome completo"
              />
            </div>
            <div class="form-group full">
              <label>Endereço</label>
              <input
                v-model="form.Endereco"
                class="glass-input"
                placeholder="Rua, número, bairro, cidade..."
              />
            </div>
            <div class="form-group full">
              <label>Descrição do Pet</label>
              <textarea
                v-model="form.Descricao"
                rows="3"
                class="glass-input"
                placeholder="Personalidade, características marcantes..."
              ></textarea>
            </div>
            <div class="form-group full">
              <label>Observações</label>
              <textarea
                v-model="form.Observacoes"
                rows="2"
                class="glass-input"
                placeholder="Alergias, medicamentos, restrições..."
              ></textarea>
            </div>
          </div>

          <div v-if="erro" class="form-error">
            <v-icon dark size="15">mdi-alert-circle-outline</v-icon>
            {{ erro }}
          </div>

          <div class="completar-actions">
            <p class="save-warning">
              <v-icon dark size="14">mdi-information-outline</v-icon>
              Após salvar, este link expira e não poderá mais ser editado por
              aqui.
            </p>
            <button
              type="submit"
              class="btn-primary salvar-btn"
              :disabled="salvando"
            >
              <v-icon dark size="17" :class="{ spin: salvando }">
                {{
                  salvando ? "mdi-loading" : "mdi-content-save-check-outline"
                }}
              </v-icon>
              {{ salvando ? "Salvando..." : "SALVAR CADASTRO" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const FORM_VAZIO = () => ({
  Nome: "",
  TipoPet: "",
  Raca: "",
  Sexo: "",
  Idade: null,
  DataNascimento: "",
  Coloracao: "",
  Vacinas: "",
  Portador: "",
  Endereco: "",
  Descricao: "",
  Observacoes: "",
  B64Foto: "",
});

export default {
  name: "CompletarRegistroView",

  data() {
    return {
      estado: "loading", // loading | invalido | form | sucesso
      token: "",
      microchipId: "",
      form: FORM_VAZIO(),
      fotoPreview: "",
      salvando: false,
      erro: "",
    };
  },

  mounted() {
    this.token = this.$route.params.token;
    this.carregarPet();
  },

  methods: {
    async carregarPet() {
      try {
        const { data } = await axios.get(
          `${this.$api}/api/completar/${this.token}`,
        );
        this.microchipId = data.IdMicrochip || "";
        // Pré-preenche campos caso existam
        Object.keys(this.form).forEach((k) => {
          if (data[k] !== undefined && data[k] !== null) this.form[k] = data[k];
        });
        this.estado = "form";
      } catch {
        this.estado = "invalido";
      }
    },

    onFotoChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.fotoPreview = e.target.result;
        this.form.B64Foto = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    async salvar() {
      this.erro = "";
      this.salvando = true;
      try {
        await axios.put(`${this.$api}/api/completar/${this.token}`, this.form);
        this.estado = "sucesso";
      } catch (err) {
        this.erro =
          err.response?.data?.msg || "Erro ao salvar. Tente novamente.";
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>

<style scoped>
.completar-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 28px 16px;
}

.completar-center {
  width: 100%;
  max-width: 440px;
  position: relative;
  z-index: 1;
}

.completar-wide {
  max-width: 680px;
}

/* State cards */
.state-card {
  border-radius: 28px;
  padding: 56px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  text-align: center;
}

.state-card h3 {
  color: #fff;
  font-size: 1.2rem;
  margin: 0;
}

.state-card p {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.84rem;
  line-height: 1.6;
  margin: 0;
  max-width: 280px;
}

.state-card strong {
  color: var(--primary-light, #5fd1cb);
}

.state-btn {
  text-decoration: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

/* Success card */
.success-card {
  border-color: rgba(44, 183, 177, 0.3) !important;
}

.success-check {
  background: rgba(44, 183, 177, 0.1);
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.home-link-sm {
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.78rem;
  text-decoration: none;
  transition: color 0.2s;
}

.home-link-sm:hover {
  color: rgba(255, 255, 255, 0.7);
}

/* Main card */
.completar-card {
  border-radius: 28px;
  padding: 36px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.completar-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.completar-logo {
  width: 44px;
  height: 44px;
  object-fit: contain;
  filter: drop-shadow(0 2px 8px rgba(44, 183, 177, 0.4));
  flex-shrink: 0;
}

.completar-title {
  color: #fff;
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0;
}

.completar-sub {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.78rem;
  margin: 5px 0 0;
}

.chip-code {
  color: var(--primary-light, #5fd1cb);
  font-family: monospace;
  letter-spacing: 1px;
}

.completar-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
}

/* Foto upload */
.foto-section {
  display: flex;
  justify-content: center;
}

.foto-preview-wrap {
  width: 130px;
  height: 130px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px dashed rgba(255, 255, 255, 0.25);
  cursor: pointer;
  position: relative;
  transition: border-color 0.2s;
  background: rgba(255, 255, 255, 0.05);
}

.foto-preview-wrap.has-foto {
  border-style: solid;
  border-color: rgba(44, 183, 177, 0.5);
}

.foto-preview-wrap:hover .foto-overlay {
  opacity: 1;
}

.foto-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.foto-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.72rem;
  text-align: center;
  padding: 8px;
}

.foto-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  opacity: 0;
  transition: opacity 0.2s;
  color: white;
  font-size: 0.72rem;
}

/* Form */
.completar-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group.full {
  grid-column: 1 / -1;
}

.form-group label {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

/* Error */
.form-error {
  background: rgba(231, 76, 60, 0.12);
  border: 1px solid rgba(231, 76, 60, 0.3);
  border-radius: 10px;
  color: #ff8a7a;
  font-size: 0.82rem;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 7px;
}

/* Actions */
.completar-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
}

.save-warning {
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  text-align: center;
}

.salvar-btn {
  width: 100%;
  justify-content: center;
  border-radius: 14px;
  padding: 15px;
  font-size: 0.9rem;
  letter-spacing: 1px;
}

.salvar-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* Spin */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
